import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserProgressContext from "../../userProgressContext";
import { validateName, validateEmail } from "../../utils/validateInput";
import { saveUserProgress } from "../../services/saveUserProgress";

const VIEW_TITLES = {
  name: "Change Name",
  email: "Change Email",
};

const PlayerSettingsModal = ({ view, onClose }) => {
  const {
    name,
    setName,
    emailId,
    setEmailId,
    progress,
    totalScore,
    redeemedScores,
    finishedTasks,
    setProgress,
    setTotalScore,
    setLocalProgress,
    setRedeemedScores,
    setFinishedTasks,
    setIsUserProgressFetched,
    setIsStartOver,
  } = useContext(UserProgressContext);

  const navigate = useNavigate();
  const modalRef = useRef(null);

  // Name view state
  const [nameInput, setNameInput] = useState(name || "");
  const [nameError, setNameError] = useState("");
  const [nameSaved, setNameSaved] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);

  // Email view state
  const [emailInput, setEmailInput] = useState(emailId || "");
  const [emailError, setEmailError] = useState("");
  const [emailConfirmStep, setEmailConfirmStep] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleNameSave = async () => {
    const error = validateName(nameInput);
    if (error) { setNameError(error); return; }
    setNameError("");
    setNameSaving(true);
    const upperName = nameInput.toUpperCase();
    try {
      await saveUserProgress({
        name: upperName,
        emailId,
        progress,
        totalScore,
        redeemedScores,
        finishedTasks,
      });
      setName(upperName);
      sessionStorage.setItem("name", upperName);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    } catch {
      setNameError("Failed to save. Please try again.");
    } finally {
      setNameSaving(false);
    }
  };

  const handleEmailConfirm = () => {
    const error = validateEmail(emailInput);
    if (error) { setEmailError(error); return; }
    if (emailInput === emailId) { setEmailError("That's already your current email."); return; }
    setEmailError("");
    setEmailConfirmStep(true);
  };

  const handleEmailReset = () => {
    sessionStorage.clear();
    setProgress(null);
    setTotalScore(0);
    setLocalProgress(null);
    setRedeemedScores(new Set());
    setFinishedTasks(new Set());
    setIsUserProgressFetched(false);
    setIsStartOver(false);
    setName("");
    setEmailId(emailInput);
    sessionStorage.setItem("emailId", emailInput);
    onClose();
    navigate("/");
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="relative bg-cyber-bg border border-cyber-dim rounded-xl shadow-2xl w-full max-w-md mx-4 p-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold tracking-widest uppercase text-cyber-accent">
            {VIEW_TITLES[view]}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-cyber-sub hover:text-cyber-accent leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Name view */}
        {view === "name" && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-cyber-faint">
              Correct a typo or update how your name appears in the game. Your progress is not affected.
            </p>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => { setNameInput(e.target.value); setNameError(""); setNameSaved(false); }}
              placeholder="Your name"
              className="uppercase w-full px-3 py-2 bg-cyber-deep text-cyber-text rounded border border-cyber-muted focus:outline-none focus:border-cyber-accent placeholder-cyber-sub"
              autoComplete="off"
              autoFocus
            />
            {nameError && <p className="text-red-400 text-sm">{nameError}</p>}
            {nameSaved && <p className="text-green-400 text-sm">Name updated successfully!</p>}
            <button
              onClick={handleNameSave}
              disabled={nameSaving}
              className="w-full bg-cyber-panel hover:bg-cyber-muted border border-cyber-accent text-cyber-accent font-bold py-2 px-4 rounded tracking-widest uppercase transition-colors duration-200 disabled:opacity-50"
            >
              {nameSaving ? "Saving..." : "Save Name"}
            </button>
          </div>
        )}

        {/* Email view */}
        {view === "email" && (
          <div className="flex flex-col gap-4">
            {!emailConfirmStep ? (
              <>
                <p className="text-sm text-cyber-faint">
                  Your email is used to save and resume your game progress across sessions.
                </p>
                <div className="bg-amber-900/40 border border-amber-500 rounded-lg p-3 flex gap-2 items-start">
                  <span className="text-amber-400 text-lg leading-tight">⚠️</span>
                  <p className="text-amber-300 text-sm leading-snug">
                    <strong>Changing your email will reset all game progress.</strong> You will need to start the adventure from scratch.
                  </p>
                </div>
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                  placeholder="New email address"
                  className="w-full px-3 py-2 bg-cyber-deep text-cyber-text rounded border border-cyber-muted focus:outline-none focus:border-cyber-accent placeholder-cyber-sub"
                  autoComplete="off"
                  autoFocus
                />
                {emailError && <p className="text-red-400 text-sm">{emailError}</p>}
                <button
                  onClick={handleEmailConfirm}
                  className="w-full bg-amber-700 hover:bg-amber-600 border border-amber-400 text-white font-bold py-2 px-4 rounded tracking-widest uppercase transition-colors duration-200"
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <div className="bg-red-900/40 border border-red-500 rounded-lg p-4 flex flex-col gap-2">
                  <p className="text-red-300 font-bold text-sm uppercase tracking-wider">Are you sure?</p>
                  <p className="text-red-200 text-sm">
                    Switching to <span className="font-semibold text-white">{emailInput}</span> will erase all your current progress and scores. This cannot be undone.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setEmailConfirmStep(false)}
                    className="flex-1 bg-cyber-panel hover:bg-cyber-muted border border-cyber-dim text-cyber-sub font-bold py-2 px-4 rounded tracking-widest uppercase transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEmailReset}
                    className="flex-1 bg-red-800 hover:bg-red-700 border border-red-500 text-white font-bold py-2 px-4 rounded tracking-widest uppercase transition-colors duration-200"
                  >
                    Reset &amp; Switch
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSettingsModal;
